// ============================================================================
// FSA SIGNATURE WORKFLOW HOOKS
// ============================================================================
// Custom hooks for the 4 Signature Workflow entities — Audit Firm,
// Audit Partner, Director Signatory, Engagement Signature Panel.
// Mirrors useFsaTemplates / useFsaEngagementSettings shape so screens
// follow the same data-access conventions.

import { useCallback, useEffect, useState } from 'react';
import {
  fsaAuditFirmService,
  fsaAuditPartnerService,
  fsaDirectorSignatoryService,
  fsaEngagementSignaturePanelService,
} from 'api/services/fsa/fsaSignatureService';
import type {
  AuditFirm, AuditFirmSearchRequest,
  AuditPartner, AuditPartnerSearchRequest,
  DirectorSignatory, DirectorSignatorySearchRequest,
  EngagementSignaturePanel, UpsertEngagementSignaturePanelRequest,
} from 'api/types/fsa/signature.types';
import type { ID } from 'api/types';

interface Pagination {
  currentPage: number;
  pageSize: number;
  totalPages: number;
  totalItems: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

const EMPTY_PAGINATION: Pagination = {
  currentPage: 1, pageSize: 20, totalPages: 0, totalItems: 0, hasNextPage: false, hasPreviousPage: false,
};

// ---------------------------------------------------------------------------
// Audit Firm
// ---------------------------------------------------------------------------

export function useFsaAuditFirms(params?: AuditFirmSearchRequest) {
  const [data, setData] = useState<AuditFirm[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [pagination, setPagination] = useState<Pagination>(EMPTY_PAGINATION);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await fsaAuditFirmService.getAll(params);
      setData(resp.data);
      setPagination(resp.pagination as Pagination);
      setError(null);
    } catch (err) {
      setError(err as Error);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [JSON.stringify(params)]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetch(); }, [fetch]);
  return { data, loading, error, pagination, refetch: fetch };
}

export function useFsaAuditFirm(id: ID | null | undefined) {
  const [data, setData] = useState<AuditFirm | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetch = useCallback(async () => {
    if (!id) { setData(null); return; }
    setLoading(true);
    try {
      const resp = await fsaAuditFirmService.getById(id);
      setData(resp.data ?? null);
      setError(null);
    } catch (err) {
      setError(err as Error);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetch(); }, [fetch]);
  return { data, loading, error, refetch: fetch };
}

// ---------------------------------------------------------------------------
// Audit Partner
// ---------------------------------------------------------------------------

export function useFsaAuditPartners(params?: AuditPartnerSearchRequest) {
  const [data, setData] = useState<AuditPartner[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [pagination, setPagination] = useState<Pagination>(EMPTY_PAGINATION);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await fsaAuditPartnerService.getAll(params);
      setData(resp.data);
      setPagination(resp.pagination as Pagination);
      setError(null);
    } catch (err) {
      setError(err as Error);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [JSON.stringify(params)]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetch(); }, [fetch]);
  return { data, loading, error, pagination, refetch: fetch };
}

// ---------------------------------------------------------------------------
// Director Signatory
// ---------------------------------------------------------------------------

export function useFsaDirectorSignatories(params?: DirectorSignatorySearchRequest) {
  const [data, setData] = useState<DirectorSignatory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [pagination, setPagination] = useState<Pagination>(EMPTY_PAGINATION);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await fsaDirectorSignatoryService.getAll(params);
      setData(resp.data);
      setPagination(resp.pagination as Pagination);
      setError(null);
    } catch (err) {
      setError(err as Error);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [JSON.stringify(params)]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetch(); }, [fetch]);
  return { data, loading, error, pagination, refetch: fetch };
}

// ---------------------------------------------------------------------------
// Engagement Signature Panel — single-row-per-engagement (no pagination).
// ---------------------------------------------------------------------------

export function useFsaEngagementSignaturePanel(engagementId: ID | null | undefined) {
  const [data, setData] = useState<EngagementSignaturePanel | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetch = useCallback(async () => {
    if (!engagementId) { setData(null); return; }
    setLoading(true);
    try {
      const panel = await fsaEngagementSignaturePanelService.getByEngagement(engagementId);
      setData(panel);
      setError(null);
    } catch (err) {
      setError(err as Error);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [engagementId]);

  useEffect(() => { fetch(); }, [fetch]);
  return { data, loading, error, refetch: fetch };
}

/** Imperative carry-forward draft loader — invoked on first-open of a new engagement's panel. */
export function useFsaSignaturePanelCarryForward() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const load = useCallback(async (engagementId: ID): Promise<UpsertEngagementSignaturePanelRequest | null> => {
    setLoading(true);
    try {
      const draft = await fsaEngagementSignaturePanelService.getCarryForwardDraft(engagementId);
      setError(null);
      return draft;
    } catch (err) {
      setError(err as Error);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return { load, loading, error };
}
